/**
 * 💬 Report — in-app tool feedback with an annotated screenshot. Ported from topgrading-webapp.
 *
 * Click → auto-captures the page you're on (modern-screenshot, BEFORE the modal opens so it isn't in
 * the shot) → you box/pen the problem in red → it's stored in tool_feedback and shows in the 🐞 Issues
 * queue. A "full screen" fallback uses the browser's screen-capture for cases the DOM render can't
 * handle. Internal staff only (App gates rendering); any logged-in staffer can report.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { shrinkDataUrl, captureViewport, captureScreen } from '../lib/screenshot';
import { Modal } from './ui/Modal';

type Tool = 'box' | 'pen';
interface Shape { tool: Tool; pts: { x: number; y: number }[]; }

export default function ReportButton() {
  const loc = useLocation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<'bug' | 'feature'>('bug');
  const [body, setBody] = useState('');
  const [done, setDone] = useState(false);
  const [img, setImg] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [tool, setTool] = useState<Tool>('box');
  const [imageDropped, setImageDropped] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const bgRef = useRef<HTMLImageElement | null>(null);
  const shapesRef = useRef<Shape[]>([]);
  const drawingRef = useRef(false);

  const redraw = useCallback(() => {
    const c = canvasRef.current, bg = bgRef.current; if (!c || !bg) return;
    const ctx = c.getContext('2d'); if (!ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.drawImage(bg, 0, 0, c.width, c.height);
    ctx.strokeStyle = '#ff2d2d'; ctx.lineWidth = 3; ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    for (const s of shapesRef.current) {
      const a = s.pts[0], b = s.pts[s.pts.length - 1];
      if (!a || !b) continue;
      if (s.tool === 'box' && s.pts.length >= 2) {
        ctx.strokeRect(a.x, a.y, b.x - a.x, b.y - a.y);
      } else if (s.tool === 'pen') {
        ctx.beginPath(); ctx.moveTo(a.x, a.y);
        for (const p of s.pts.slice(1)) ctx.lineTo(p.x, p.y);
        ctx.stroke();
      }
    }
  }, []);

  const loadBg = useCallback((dataUrl: string) => {
    const im = new Image();
    im.onload = () => {
      const c = canvasRef.current; if (!c) return;
      // Keep the canvas at the image's NATIVE resolution (crisp); CSS scales it to fit the modal.
      // Mouse coords are mapped via getBoundingClientRect, so display scaling doesn't matter.
      c.width = im.width; c.height = im.height;
      bgRef.current = im; shapesRef.current = []; redraw();
    };
    im.src = dataUrl;
  }, [redraw]);

  // Load the captured screenshot into the canvas once the modal (and canvas) is mounted.
  useEffect(() => { if (open && img) loadBg(img); }, [open, img, loadBg]);

  const openModal = async () => {
    setDone(false); setBody(''); shapesRef.current = []; setImg(null); setCapturing(true);
    // Capture BEFORE the modal opens so it isn't in the shot.
    const shot = await captureViewport();
    if (shot) setImg(shot);
    setCapturing(false);
    setOpen(true);
  };

  const onCaptureScreen = async () => {
    const url = await captureScreen();
    if (url) { setImg(url); loadBg(url); }
  };

  const at = (e: React.MouseEvent) => {
    const c = canvasRef.current!, r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) };
  };
  const onDown = (e: React.MouseEvent) => { if (!bgRef.current) return; drawingRef.current = true; shapesRef.current.push({ tool, pts: [at(e)] }); };
  const onMove = (e: React.MouseEvent) => {
    if (!drawingRef.current) return;
    const s = shapesRef.current[shapesRef.current.length - 1];
    if (!s) return;
    if (tool === 'box') { const start = s.pts[0]; s.pts = start ? [start, at(e)] : [at(e)]; }
    else s.pts.push(at(e));
    redraw();
  };
  const onUp = () => { drawingRef.current = false; };

  const submit = useMutation({
    mutationFn: async () => {
      setImageDropped(false);
      const raw = (canvasRef.current && bgRef.current)
        ? canvasRef.current.toDataURL('image/jpeg', 0.85)
        : (img ?? undefined);
      // Shrink so it clears body/proxy limits. Then send; if it STILL fails with the image attached,
      // resend text-only so a report is never silently lost.
      const shot = raw ? await shrinkDataUrl(raw) : undefined;
      const send = (screenshot?: string) => api('/tool-feedback', {
        method: 'POST',
        body: JSON.stringify({ kind, body: body.trim(), page_url: loc.pathname, screenshot }),
      });
      try {
        return await send(shot);
      } catch (e) {
        if (shot) { setImageDropped(true); return await send(undefined); }
        throw e;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tool-feedback'] });
      setDone(true); setBody('');
    },
  });

  const close = () => { setOpen(false); setDone(false); setImg(null); setImageDropped(false); bgRef.current = null; shapesRef.current = []; submit.reset(); };
  const toolBtn = (t: Tool, label: string) => (
    <button type="button" onClick={() => setTool(t)}
      style={{ fontSize: 12, fontWeight: tool === t ? 700 : 400, borderColor: tool === t ? 'var(--accent)' : undefined }}>
      {label}
    </button>
  );

  return (
    <>
      {/* Fixed floating button — visible on every page, never scrolls off. Below the modal (z 1000). */}
      <button type="button" onClick={openModal} disabled={capturing} className="fab"
        title="Report a bug or request a feature · 报告问题/建议"
        style={{
          position: 'fixed', right: 20, bottom: 20, zIndex: 900,
          fontSize: 13, fontWeight: 600, padding: '10px 16px', borderRadius: 999,
          background: 'var(--accent, #8a6d3b)', color: '#fff', border: 'none',
          cursor: capturing ? 'progress' : 'pointer', boxShadow: '0 4px 16px rgba(0,0,0,0.22)',
        }}>
        {capturing ? '📸 ' : '💬 '}<span className="fab-label">{capturing ? '截图中… / Capturing…' : '反馈 / Report'}</span>
      </button>

      {open && (
        <Modal onClose={close} align="center" width={820}>
            {done ? (
              <div style={{ textAlign: 'center', padding: 12 }}>
                <div style={{ fontSize: 28 }}>✅</div>
                <p style={{ margin: '8px 0' }}>
                  {imageDropped
                    ? 'Sent ✅ — the screenshot was too large to attach, so your note was sent without it. · 已发送(截图过大未附上,文字说明已送达)。'
                    : 'Thanks — sent to the developer with your screenshot. · 已连同截图发送,谢谢!'}
                </p>
                <button type="button" className="btn-primary" onClick={close}>Close · 关闭</button>
              </div>
            ) : (
              <>
                <h3 style={{ marginTop: 0 }}>💬 Report an issue or request a feature · 报告问题 / 建议功能</h3>
                <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
                  Circle the problem on the screenshot, add a note, and send. · 在截图上圈出问题,写一句说明并发送。
                </p>
                <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                  <label style={{ cursor: 'pointer' }}><input type="radio" checked={kind === 'bug'} onChange={() => setKind('bug')} /> 🐞 Bug · 问题</label>
                  <label style={{ cursor: 'pointer' }}><input type="radio" checked={kind === 'feature'} onChange={() => setKind('feature')} /> ✨ Feature · 建议</label>
                </div>

                {img ? (
                  <>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                      <span className="muted" style={{ fontSize: 12 }}>Draw · 标注:</span>
                      {toolBtn('box', '▭ Box · 框')}
                      {toolBtn('pen', '✏️ Pen · 笔')}
                      <button type="button" style={{ fontSize: 12 }} onClick={() => { shapesRef.current.pop(); redraw(); }}>Undo · 撤销</button>
                      <button type="button" style={{ fontSize: 12 }} onClick={() => { shapesRef.current = []; redraw(); }}>Clear · 清除</button>
                      <button type="button" style={{ fontSize: 12, marginLeft: 'auto' }} onClick={onCaptureScreen} title="Capture the full screen instead">🖥 Full screen · 全屏</button>
                    </div>
                    <canvas ref={canvasRef} onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
                      style={{ width: '100%', border: '1px solid var(--rule)', borderRadius: 6, cursor: 'crosshair', display: 'block' }} />
                  </>
                ) : (
                  <div className="muted" style={{ padding: 12, border: '1px dashed var(--rule)', borderRadius: 6, fontSize: 13 }}>
                    No screenshot captured. You can still send a text report, or
                    <button type="button" style={{ fontSize: 12, marginLeft: 6 }} onClick={onCaptureScreen}>🖥 capture the screen</button>.
                  </div>
                )}

                <textarea value={body} onChange={e => setBody(e.target.value)} rows={3}
                  placeholder="What happened, or what would help? · 描述问题或你希望增加的功能"
                  style={{ width: '100%', boxSizing: 'border-box', fontSize: 14, padding: 8, marginTop: 8 }} />
                {submit.isError && <div role="alert" style={{ color: 'var(--bad)', fontSize: 13 }}>Couldn't send — try again. · 发送失败,请重试。</div>}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                  <button type="button" onClick={close}>Cancel · 取消</button>
                  <button type="button" className="btn-primary" disabled={!body.trim() || submit.isPending} onClick={() => submit.mutate()}>
                    {submit.isPending ? 'Sending… · 发送中' : 'Send · 发送'}
                  </button>
                </div>
              </>
            )}
        </Modal>
      )}
    </>
  );
}
